import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function IrishTaxSystemAmericans() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openRelocationExpert = () => {
    const expertButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (expertButton) {
      expertButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      <article className="pt-24 pb-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A381F] to-emerald-800 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-emerald-200 mb-4">
              <a href="/blog" className="hover:text-white transition-colors">Blog</a>
              <i className="ri-arrow-right-s-line"></i>
              <span>Tax Guide</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Irish Tax System Guide for Americans: Everything You Need to Know in 2024
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-emerald-100">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                January 15, 2024
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                15 min read
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                Tax & Finance Expert
              </span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
            <img
              src="https://public.readdy.ai/ai/img_res/74374b74b7e12711028768000bf08d8d.jpg"
              alt="American reviewing Irish tax documents for relocation planning"
              className="w-full h-96 object-cover object-top"
            />
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Moving to Ireland from the United States means navigating a completely different tax system. For Americans relocating to Ireland, understanding Irish taxation is crucial for financial planning and avoiding costly mistakes. This comprehensive guide explains everything Americans need to know about the Irish tax system, from PAYE to USC, and how it compares to the US tax system.
            </p>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-alert-line text-2xl text-amber-600 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-amber-900 mb-2">Important for Americans</h3>
                  <p className="text-amber-800 mb-0">
                    As a US citizen, you must file US tax returns even while living in Ireland. However, the US-Ireland tax treaty prevents double taxation. This guide covers Irish taxes; consult a US tax professional for American obligations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-list-check text-[#0A381F]"></i>
              What You'll Learn
            </h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {[
                'Irish Tax System Overview for Americans',
                'PAYE (Pay As You Earn) Explained',
                'Understanding USC and PRSI',
                'Tax Rates and Brackets in Ireland',
                'PPS Number: Your Irish Tax ID',
                'Tax Credits and Reliefs for Americans',
                'Filing Irish Tax Returns',
                'US-Ireland Tax Treaty Benefits',
                'Avoiding Double Taxation',
                'Common Tax Mistakes Americans Make',
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-[#0A381F] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Main Content Sections */}
          
          {/* Section 1: Irish Tax System Overview */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">1</span>
              Irish Tax System Overview for Americans
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              The Irish tax system operates very differently from the United States. For Americans relocating to Ireland, the most significant difference is that Ireland uses a PAYE (Pay As You Earn) system where taxes are automatically deducted from your paycheck by your employer. Unlike the US where Americans file annual returns and may owe additional taxes, most Irish workers have their taxes fully settled through payroll deductions.
            </p>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Key Differences: USA vs Ireland Tax Systems</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Aspect</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">United States</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Ireland</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Tax Collection</td>
                      <td className="py-3 px-4">Withholding + Annual Filing</td>
                      <td className="py-3 px-4">PAYE (automatic deduction)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Tax ID</td>
                      <td className="py-3 px-4">Social Security Number (SSN)</td>
                      <td className="py-3 px-4">PPS Number</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Income Tax Rates</td>
                      <td className="py-3 px-4">10% - 37% (7 brackets)</td>
                      <td className="py-3 px-4">20% - 40% (2 brackets)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Social Insurance</td>
                      <td className="py-3 px-4">FICA (7.65%)</td>
                      <td className="py-3 px-4">PRSI (4%)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Healthcare Tax</td>
                      <td className="py-3 px-4">Medicare (included in FICA)</td>
                      <td className="py-3 px-4">USC (0.5% - 8%)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Tax Year</td>
                      <td className="py-3 px-4">January 1 - December 31</td>
                      <td className="py-3 px-4">January 1 - December 31</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-semibold">Filing Deadline</td>
                      <td className="py-3 px-4">April 15</td>
                      <td className="py-3 px-4">October 31 (online)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-lg text-gray-700">
              For Americans, the Irish system is generally simpler for employees. Your employer handles most tax obligations automatically, and many Americans find they don't need to file an annual return unless they have additional income sources or want to claim specific tax reliefs.
            </p>
          </section>

          {/* Section 2: PAYE System */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">2</span>
              PAYE (Pay As You Earn) Explained for Americans
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              PAYE is Ireland's system for collecting income tax directly from your paycheck. For Americans used to the US withholding system, PAYE is similar but more comprehensive. When you start a job in Ireland, your employer automatically deducts income tax, USC (Universal Social Charge), and PRSI (social insurance) from your gross salary.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-emerald-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-check-double-line text-emerald-600"></i>
                  PAYE Benefits for Americans
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Automatic tax calculation - no quarterly estimates needed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Employer handles all deductions and payments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Real-time tax credits applied to each paycheck</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Less paperwork for most Americans</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-alert-line text-amber-600"></i>
                  What Americans Need to Do
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Obtain a PPS number before starting work</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Register with Revenue (Irish IRS equivalent)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Provide PPS number to your employer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Check your tax credits are correctly applied</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-blue-900 mb-3">Understanding Your Irish Payslip</h3>
              <p className="text-blue-800 mb-4">
                Your Irish payslip will look different from American paystubs. Here's what Americans should look for:
              </p>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start gap-2">
                  <strong className="flex-shrink-0">Gross Pay:</strong>
                  <span>Your total salary before deductions (like US gross wages)</span>
                </li>
                <li className="flex items-start gap-2">
                  <strong className="flex-shrink-0">Income Tax:</strong>
                  <span>Deducted at 20% or 40% depending on your income bracket</span>
                </li>
                <li className="flex items-start gap-2">
                  <strong className="flex-shrink-0">USC:</strong>
                  <span>Universal Social Charge (similar to Medicare tax)</span>
                </li>
                <li className="flex items-start gap-2">
                  <strong className="flex-shrink-0">PRSI:</strong>
                  <span>Social insurance (similar to Social Security tax)</span>
                </li>
                <li className="flex items-start gap-2">
                  <strong className="flex-shrink-0">Net Pay:</strong>
                  <span>Your take-home pay after all deductions</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3: Tax Rates and Brackets */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">3</span>
              Irish Tax Rates and Brackets for Americans in 2024
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Ireland has a simpler tax bracket system than the United States. Instead of seven tax brackets like in America, Ireland uses just two rates: the standard rate (20%) and the higher rate (40%). For Americans relocating to Ireland, understanding these brackets is essential for salary negotiations and financial planning.
            </p>

            <div className="bg-white border-2 border-[#0A381F] rounded-xl p-6 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">2024 Irish Income Tax Rates</h3>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-emerald-50 rounded-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">Single Person (American)</h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-gray-700">First €40,000</span>
                        <span className="text-2xl font-bold text-emerald-600">20%</span>
                      </div>
                      <p className="text-sm text-gray-600">Approximately $43,600 USD</p>
                    </div>
                    <div className="border-t border-emerald-200 pt-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-gray-700">Above €40,000</span>
                        <span className="text-2xl font-bold text-red-600">40%</span>
                      </div>
                      <p className="text-sm text-gray-600">All income over $43,600 USD</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">Married Couple (Both Working)</h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-gray-700">First €80,000</span>
                        <span className="text-2xl font-bold text-emerald-600">20%</span>
                      </div>
                      <p className="text-sm text-gray-600">Approximately $87,200 USD combined</p>
                    </div>
                    <div className="border-t border-blue-200 pt-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-gray-700">Above €80,000</span>
                        <span className="text-2xl font-bold text-red-600">40%</span>
                      </div>
                      <p className="text-sm text-gray-600">Combined income over $87,200 USD</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-0">
                  <strong>Note for Americans:</strong> These rates apply only to income tax. USC and PRSI are additional charges calculated separately. Total effective tax rate for Americans in Ireland typically ranges from 30-52% depending on income level.
                </p>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">USC (Universal Social Charge) Rates for Americans</h3>
              <p className="text-gray-700 mb-4">
                USC is an additional tax that Americans in Ireland must pay. It's similar to Medicare tax in the US but with progressive rates:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Income Level (EUR)</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Approx. USD</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">USC Rate</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">First €12,012</td>
                      <td className="py-3 px-4">$13,093</td>
                      <td className="py-3 px-4 font-semibold text-emerald-600">0.5%</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">€12,013 - €22,920</td>
                      <td className="py-3 px-4">$13,094 - $24,982</td>
                      <td className="py-3 px-4 font-semibold text-emerald-600">2%</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">€22,921 - €70,044</td>
                      <td className="py-3 px-4">$24,983 - $76,348</td>
                      <td className="py-3 px-4 font-semibold text-amber-600">4.5%</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Above €70,044</td>
                      <td className="py-3 px-4">Above $76,348</td>
                      <td className="py-3 px-4 font-semibold text-red-600">8%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">PRSI (Social Insurance) for Americans</h3>
              <p className="text-gray-700 mb-4">
                PRSI is Ireland's social insurance system, similar to Social Security in the United States. Most American employees in Ireland pay:
              </p>
              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-semibold text-gray-900">Employee PRSI Rate:</span>
                  <span className="text-3xl font-bold text-blue-600">4%</span>
                </div>
                <p className="text-sm text-gray-600">
                  Applied to all income (no cap like US Social Security). Your employer also pays 8.8-11.05% on your behalf.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Real Examples */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">4</span>
              Real Tax Examples for Americans in Ireland
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Let's look at real-world examples of what Americans actually pay in Irish taxes at different salary levels. These calculations include income tax, USC, and PRSI to show your true take-home pay.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Example 1 */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="bg-emerald-100 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Software Engineer</h3>
                  <p className="text-2xl font-bold text-emerald-700">€60,000 / year</p>
                  <p className="text-sm text-gray-600">Approximately $65,400 USD</p>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">Gross Salary</span>
                    <span className="font-semibold">€60,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">Income Tax</span>
                    <span className="font-semibold text-red-600">-€10,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">USC</span>
                    <span className="font-semibold text-red-600">-€2,468</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">PRSI</span>
                    <span className="font-semibold text-red-600">-€2,400</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 bg-emerald-50 rounded-lg p-3">
                    <span className="font-bold text-gray-900">Net Take-Home</span>
                    <span className="text-xl font-bold text-emerald-700">€45,132</span>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Effective Tax Rate:</span>
                    <span className="font-bold text-gray-900">24.8%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Monthly Take-Home:</span>
                    <span className="font-bold text-gray-900">€3,761 ($4,100 USD)</span>
                  </div>
                </div>
              </div>

              {/* Example 2 */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="bg-blue-100 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Senior Manager</h3>
                  <p className="text-2xl font-bold text-blue-700">€90,000 / year</p>
                  <p className="text-sm text-gray-600">Approximately $98,100 USD</p>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">Gross Salary</span>
                    <span className="font-semibold">€90,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">Income Tax</span>
                    <span className="font-semibold text-red-600">-€28,000</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">USC</span>
                    <span className="font-semibold text-red-600">-€5,068</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-700">PRSI</span>
                    <span className="font-semibold text-red-600">-€3,600</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 bg-blue-50 rounded-lg p-3">
                    <span className="font-bold text-gray-900">Net Take-Home</span>
                    <span className="text-xl font-bold text-blue-700">€53,332</span>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Effective Tax Rate:</span>
                    <span className="font-bold text-gray-900">40.7%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Monthly Take-Home:</span>
                    <span className="font-bold text-gray-900">€4,444 ($4,844 USD)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-amber-900 mb-3">Comparing to US Taxes</h3>
              <p className="text-amber-800 mb-3">
                Americans often ask: "Will I pay more taxes in Ireland than in the US?" The answer depends on your state and income level:
              </p>
              <ul className="space-y-2 text-amber-800">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Lower income Americans</strong> (under $50k) may pay slightly more in Ireland</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>Middle income Americans</strong> ($50k-$100k) pay similar rates, especially from high-tax US states</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>High income Americans</strong> (over $100k) often pay more in Ireland due to the 40% rate</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span><strong>However:</strong> Americans in Ireland get free/low-cost healthcare, more vacation days, and better work-life balance</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 5: PPS Number */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">5</span>
              Getting Your PPS Number: Essential for Americans
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Your PPS (Personal Public Service) number is Ireland's equivalent to the American Social Security Number. Every American working in Ireland must have a PPS number before starting employment. Without it, you'll be taxed at the emergency rate (approximately 40% on all income with no tax credits).
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white border-2 border-[#0A381F] rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-[#0A381F]"></i>
                  Documents Americans Need
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0A381F] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                    <div>
                      <p className="font-semibold text-gray-900">Valid US Passport</p>
                      <p className="text-sm text-gray-600">Must be current and valid</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0A381F] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                    <div>
                      <p className="font-semibold text-gray-900">Proof of Address in Ireland</p>
                      <p className="text-sm text-gray-600">Utility bill, lease agreement, or bank statement</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0A381F] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                    <div>
                      <p className="font-semibold text-gray-900">Job Offer Letter or Employment Contract</p>
                      <p className="text-sm text-gray-600">From your Irish employer</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0A381F] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">4</div>
                    <div>
                      <p className="font-semibold text-gray-900">Birth Certificate (if applicable)</p>
                      <p className="text-sm text-gray-600">May be required for certain applications</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-map-pin-line text-[#D4AF37]"></i>
                  Application Process for Americans
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0 font-bold">1</div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Book Appointment Online</p>
                      <p className="text-sm text-gray-600">Visit MyWelfare.ie to schedule at your local Intreo Centre</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0 font-bold">2</div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Attend In-Person Appointment</p>
                      <p className="text-sm text-gray-600">Bring all required documents (originals, not copies)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0 font-bold">3</div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Receive PPS Number</p>
                      <p className="text-sm text-gray-600">Usually issued immediately or within 5-10 business days</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0 font-bold">4</div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Provide to Employer</p>
                      <p className="text-sm text-gray-600">Give your PPS number to HR to set up correct tax deductions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-red-900 mb-3 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                Emergency Tax Warning for Americans
              </h3>
              <p className="text-red-800 mb-3">
                If you start working in Ireland without a PPS number, you'll be placed on "emergency tax" which means:
              </p>
              <ul className="space-y-2 text-red-800">
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 flex-shrink-0 mt-1"></i>
                  <span>You'll pay approximately 40% tax on ALL income (no standard rate band)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 flex-shrink-0 mt-1"></i>
                  <span>You won't receive any tax credits, significantly reducing your take-home pay</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 flex-shrink-0 mt-1"></i>
                  <span>You'll need to file for a refund later (time-consuming process for Americans)</span>
                </li>
              </ul>
              <p className="text-red-800 mt-3 font-semibold">
                Americans should apply for their PPS number IMMEDIATELY upon arriving in Ireland, even before starting work.
              </p>
            </div>
          </section>

          {/* Section 6: Tax Credits */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">6</span>
              Tax Credits and Reliefs for Americans in Ireland
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Irish tax credits work differently than American tax deductions. In Ireland, tax credits directly reduce the amount of tax you pay (similar to US tax credits, not deductions). Americans in Ireland are entitled to several automatic tax credits that significantly reduce their tax burden.
            </p>

            <div className="bg-white border-2 border-[#0A381F] rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Standard Tax Credits for Americans (2024)</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-emerald-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-gray-900">Personal Tax Credit</h4>
                    <span className="text-2xl font-bold text-emerald-600">€1,775</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Every American working in Ireland automatically receives this credit. It reduces your annual tax bill by €1,775 ($1,935 USD).
                  </p>
                  <div className="bg-white rounded p-3">
                    <p className="text-xs text-gray-600 mb-1">Monthly Benefit:</p>
                    <p className="text-lg font-bold text-emerald-700">€148 / month</p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-gray-900">Employee Tax Credit</h4>
                    <span className="text-2xl font-bold text-blue-600">€1,775</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Additional credit for Americans who are employees (not self-employed). Combined with personal credit = €3,550 total.
                  </p>
                  <div className="bg-white rounded p-3">
                    <p className="text-xs text-gray-600 mb-1">Monthly Benefit:</p>
                    <p className="text-lg font-bold text-blue-700">€148 / month</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Total Standard Credits for American Employees:</strong> €3,550/year (€296/month) - This means the first €17,750 of income is effectively tax-free for Americans in Ireland.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-parent-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Home Carer Credit</h4>
                <p className="text-2xl font-bold text-purple-600 mb-3">Up to €1,700</p>
                <p className="text-sm text-gray-600">
                  For American families where one spouse stays home to care for children or dependents. Income limits apply.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-parent-fill text-2xl text-pink-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Single Person Child Carer</h4>
                <p className="text-2xl font-bold text-pink-600 mb-3">€1,650</p>
                <p className="text-sm text-gray-600">
                  For single American parents raising children in Ireland. Significant tax relief for single-parent families.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-hospital-line text-2xl text-orange-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Medical Expenses Relief</h4>
                <p className="text-2xl font-bold text-orange-600 mb-3">20% back</p>
                <p className="text-sm text-gray-600">
                  Americans can claim 20% tax relief on qualifying medical expenses not covered by insurance.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-blue-900 mb-3">How to Claim Tax Credits as an American</h3>
              <p className="text-blue-800 mb-4">
                Most standard credits (Personal and Employee) are applied automatically through PAYE. However, Americans should:
              </p>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 flex-shrink-0 mt-1"></i>
                  <span>Register for Revenue's myAccount online portal (like IRS.gov for Americans)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 flex-shrink-0 mt-1"></i>
                  <span>Review your Tax Credit Certificate to ensure all credits are applied</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 flex-shrink-0 mt-1"></i>
                  <span>Claim additional credits (medical, rent relief, etc.) through myAccount</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 flex-shrink-0 mt-1"></i>
                  <span>Keep receipts for all claimable expenses (Americans should save for 6 years)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 7: US-Ireland Tax Treaty */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">7</span>
              US-Ireland Tax Treaty: Avoiding Double Taxation
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              One of the biggest concerns for Americans moving to Ireland is double taxation - paying taxes to both the United States and Ireland on the same income. Fortunately, the US-Ireland Tax Treaty prevents this for most Americans. However, US citizens must still file US tax returns annually, even while living in Ireland.
            </p>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">How the Tax Treaty Protects Americans</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-2xl text-green-600"></i>
                    </div>
                    <h4 className="font-bold text-gray-900">Foreign Earned Income Exclusion</h4>
                  </div>
                  <p className="text-gray-700 mb-3">
                    Americans living in Ireland can exclude up to <strong>$126,500 (2024)</strong> of Irish employment income from US taxation.
                  </p>
                  <div className="bg-green-50 rounded p-3">
                    <p className="text-sm text-green-800">
                      <strong>Example:</strong> If you earn €60,000 ($65,400) in Ireland, you likely owe $0 in US federal income tax.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-exchange-dollar-line text-2xl text-blue-600"></i>
                    </div>
                    <h4 className="font-bold text-gray-900">Foreign Tax Credit</h4>
                  </div>
                  <p className="text-gray-700 mb-3">
                    Americans can claim a dollar-for-dollar credit for Irish taxes paid against any US tax liability.
                  </p>
                  <div className="bg-blue-50 rounded p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Example:</strong> If you owe $5,000 US tax but paid $8,000 Irish tax, you owe $0 to the US.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">US Tax Filing Requirements for Americans in Ireland</h3>
              <p className="text-gray-700 mb-4">
                Even though you live in Ireland, you must file US taxes if your income exceeds these thresholds:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Filing Status</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">2024 Threshold</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Deadline</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">Single</td>
                      <td className="py-3 px-4 font-semibold">$14,600</td>
                      <td className="py-3 px-4">June 15 (automatic 2-month extension for Americans abroad)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">Married Filing Jointly</td>
                      <td className="py-3 px-4 font-semibold">$29,200</td>
                      <td className="py-3 px-4">June 15 (automatic 2-month extension for Americans abroad)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Head of Household</td>
                      <td className="py-3 px-4 font-semibold">$21,900</td>
                      <td className="py-3 px-4">June 15 (automatic 2-month extension for Americans abroad)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-amber-900 mb-3 flex items-center gap-2">
                  <i className="ri-file-text-line"></i>
                  Forms Americans Must File
                </h3>
                <ul className="space-y-2 text-amber-800">
                  <li className="flex items-start gap-2">
                    <strong className="flex-shrink-0">Form 1040:</strong>
                    <span>Standard US tax return</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <strong className="flex-shrink-0">Form 2555:</strong>
                    <span>Foreign Earned Income Exclusion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <strong className="flex-shrink-0">Form 1116:</strong>
                    <span>Foreign Tax Credit (if not using exclusion)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <strong className="flex-shrink-0">FBAR:</strong>
                    <span>If Irish bank accounts exceed $10,000</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <strong className="flex-shrink-0">Form 8938:</strong>
                    <span>Foreign financial assets over $200,000</span>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-red-900 mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  Penalties for Non-Compliance
                </h3>
                <p className="text-red-800 mb-3">
                  Americans in Ireland who fail to file US taxes face serious consequences:
                </p>
                <ul className="space-y-2 text-red-800">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 flex-shrink-0 mt-1"></i>
                    <span>Failure to file penalty: 5% per month (up to 25%)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 flex-shrink-0 mt-1"></i>
                    <span>FBAR penalties: Up to $10,000 per violation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 flex-shrink-0 mt-1"></i>
                    <span>Passport revocation for seriously delinquent tax debt</span>
                  </li>
                </ul>
                <p className="text-red-800 mt-3 font-semibold">
                  Americans should hire a US tax professional experienced with expat returns.
                </p>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-green-900 mb-3">Good News for Most Americans</h3>
              <p className="text-green-800 mb-3">
                The vast majority of Americans living in Ireland owe little to no US federal income tax due to:
              </p>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span>The $126,500 Foreign Earned Income Exclusion covers most salaries</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span>Irish tax rates are generally higher than US rates, so Foreign Tax Credit eliminates most liability</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                  <span>Standard deduction further reduces taxable income</span>
                </li>
              </ul>
              <p className="text-green-800 mt-3">
                <strong>Bottom line:</strong> Americans must file US returns, but most won't owe additional US tax beyond what they pay in Ireland.
              </p>
            </div>
          </section>

          {/* Section 8: Common Mistakes */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">8</span>
              Common Tax Mistakes Americans Make in Ireland
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Americans relocating to Ireland often make preventable tax mistakes that cost them money or create compliance issues. Here are the most common errors and how to avoid them:
            </p>

            <div className="space-y-6">
              {[
                {
                  number: 1,
                  title: 'Starting Work Without a PPS Number',
                  problem: 'Many Americans begin employment before obtaining their PPS number, resulting in emergency tax rates of 40% on all income with no tax credits.',
                  solution: 'Apply for your PPS number immediately upon arriving in Ireland, even before starting your job search. Book your appointment at MyWelfare.ie as soon as you have an Irish address.',
                  impact: 'Can cost Americans €500-1,500 per month in overpaid taxes until corrected.',
                },
                {
                  number: 2,
                  title: 'Not Registering for Revenue myAccount',
                  problem: 'Americans fail to create a Revenue myAccount, missing out on tax credits, reliefs, and the ability to monitor their tax status online.',
                  solution: 'Register for myAccount at revenue.ie within your first month in Ireland. You\'ll need your PPS number and can verify identity online or at a Revenue office.',
                  impact: 'Missing tax credits worth €3,550+ annually for most American employees.',
                },
                {
                  number: 3,
                  title: 'Forgetting to File US Tax Returns',
                  problem: 'Americans assume that because they live in Ireland and pay Irish taxes, they don\'t need to file US returns. This is incorrect and can lead to serious penalties.',
                  solution: 'File US tax returns annually by June 15 (automatic extension for Americans abroad). Use Form 2555 for Foreign Earned Income Exclusion or Form 1116 for Foreign Tax Credit.',
                  impact: 'Penalties starting at 5% per month, plus potential FBAR fines up to $10,000.',
                },
                {
                  number: 4,
                  title: 'Not Reporting Irish Bank Accounts to US',
                  problem: 'Americans don\'t realize they must report foreign bank accounts to the US Treasury if the combined balance exceeds $10,000 at any point during the year.',
                  solution: 'File FinCEN Form 114 (FBAR) annually by April 15 (automatic extension to October 15). This is separate from your tax return and filed through FinCEN.gov.',
                  impact: 'Willful violations can result in penalties up to $100,000 or 50% of account balance.',
                },
                {
                  number: 5,
                  title: 'Claiming Wrong Tax Credits',
                  problem: 'Americans either don\'t claim all eligible credits or incorrectly claim credits they\'re not entitled to, especially regarding marital status and dependents.',
                  solution: 'Review your Tax Credit Certificate annually through myAccount. Ensure you\'re claiming Personal Credit (€1,775), Employee Credit (€1,775), and any applicable family credits.',
                  impact: 'Either overpaying taxes by €1,000-3,000 annually or facing penalties for incorrect claims.',
                },
                {
                  number: 6,
                  title: 'Not Understanding USC Exemptions',
                  problem: 'Americans don\'t realize that if their total income is under €13,000, they\'re exempt from USC entirely. Some also miss that medical card holders have reduced USC rates.',
                  solution: 'If you\'re working part-time or have low income, ensure your employer knows you\'re USC-exempt. If you have a medical card, inform Revenue to get reduced USC rates.',
                  impact: 'Overpaying USC by €500-1,500 annually for low-income Americans.',
                },
                {
                  number: 7,
                  title: 'Mixing Up Tax Years',
                  problem: 'Americans get confused between the Irish tax year (January-December) and US tax year (also January-December but filed differently), leading to reporting errors.',
                  solution: 'Keep separate records for Irish and US tax purposes. Irish taxes are handled through PAYE; US taxes require annual filing. Consider hiring a tax professional experienced with expat returns.',
                  impact: 'Incorrect reporting can trigger audits in both countries.',
                },
                {
                  number: 8,
                  title: 'Not Claiming Medical Expenses Relief',
                  problem: 'Americans pay for medical expenses out-of-pocket but don\'t claim the 20% tax relief available on qualifying expenses not covered by insurance.',
                  solution: 'Keep all medical receipts (doctor visits, prescriptions, dental, optical). Claim relief through Revenue myAccount. You can claim for expenses from the previous 4 years.',
                  impact: 'Missing out on 20% refund on potentially thousands of euros in medical costs.',
                },
              ].map((mistake) => (
                <div key={mistake.number} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center flex-shrink-0 text-xl font-bold">
                      {mistake.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{mistake.title}</h3>
                      
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-red-600 mb-1">❌ The Problem:</p>
                        <p className="text-gray-700">{mistake.problem}</p>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-green-600 mb-1">✅ The Solution:</p>
                        <p className="text-gray-700">{mistake.solution}</p>
                      </div>
                      
                      <div className="bg-amber-50 rounded-lg p-3">
                        <p className="text-sm font-semibold text-amber-900 mb-1">💰 Financial Impact:</p>
                        <p className="text-amber-800">{mistake.impact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 9: Checklist */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#0A381F] text-white rounded-lg flex items-center justify-center text-xl flex-shrink-0">9</span>
              Tax Checklist for Americans Moving to Ireland
            </h2>
            
            <div className="bg-gradient-to-br from-[#0A381F] to-emerald-800 text-white rounded-2xl p-8 mb-6">
              <h3 className="text-2xl font-bold mb-6 text-center">Complete Tax Setup Guide for Americans</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    Before You Arrive in Ireland
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Research US-Ireland tax treaty provisions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Find a US tax professional experienced with expat returns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Organize US tax documents for previous years</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Understand your US filing obligations while abroad</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    First Week in Ireland
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Book PPS number appointment at MyWelfare.ie</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Gather required documents (passport, proof of address, job offer)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Open Irish bank account</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Keep records of arrival date (important for tax residency)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    First Month in Ireland
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Attend PPS number appointment and receive number</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Provide PPS number to employer immediately</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Register for Revenue myAccount online</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Review your Tax Credit Certificate</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Verify correct tax deductions on first payslip</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    Ongoing (Annual Tasks)
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>File US tax return by June 15 (Form 1040, 2555)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>File FBAR if Irish accounts exceed $10,000</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Review Irish Tax Credit Certificate annually</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Claim medical expenses relief through myAccount</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-line flex-shrink-0 mt-1"></i>
                      <span>Keep all tax documents for 6 years (Irish requirement)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-blue-900 mb-3">💡 Pro Tip for Americans</h3>
              <p className="text-blue-800">
                Set up calendar reminders for key tax dates: June 15 (US return deadline for Americans abroad), October 15 (FBAR deadline), October 31 (Irish return deadline if self-employed). Missing these deadlines can be costly for Americans living in Ireland.
              </p>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Final Thoughts for Americans</h2>
              <p className="text-lg text-gray-700 mb-4">
                Understanding the Irish tax system is crucial for Americans relocating to Ireland. While it may seem complex at first, the Irish PAYE system is actually simpler than the US system for most employees. The key is to get your PPS number quickly, register for Revenue myAccount, and stay compliant with both Irish and US tax obligations.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Most Americans find that despite higher tax rates in Ireland, the benefits—including universal healthcare, generous vacation time, and better work-life balance—more than compensate for the additional tax burden. Plus, with the US-Ireland tax treaty, you won't face double taxation on your income.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Remember: while this guide provides comprehensive information for Americans, tax situations can be complex. Consider consulting with tax professionals in both Ireland and the United States to ensure you're maximizing your benefits and staying fully compliant.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openRelocationExpert}
                  className="bg-[#0A381F] text-white px-8 py-4 rounded-xl font-bold hover:bg-emerald-800 transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-question-answer-line text-xl"></i>
                  Ask Our Relocation Expert
                </button>
                <a
                  href="/tools/cost-calculator"
                  className="bg-white text-[#0A381F] px-8 py-4 rounded-xl font-bold border-2 border-[#0A381F] hover:bg-[#0A381F]/5 transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-calculator-line text-xl"></i>
                  Calculate Your Costs
                </a>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles for Americans</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="/blog/articles/healthcare-guide-americans" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover-lift group">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-heart-pulse-line text-2xl text-emerald-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0A381F] transition-colors">
                  Healthcare Guide for Americans
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Complete guide to Irish healthcare system for Americans relocating to Ireland
                </p>
                <span className="text-[#0A381F] font-semibold text-sm flex items-center gap-1">
                  Read More <i className="ri-arrow-right-line"></i>
                </span>
              </a>

              <a href="/blog/articles/cost-of-living-usa-ireland" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover-lift group">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0A381F] transition-colors">
                  Cost of Living: USA vs Ireland
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Detailed cost comparison for Americans considering relocation to Ireland
                </p>
                <span className="text-[#0A381F] font-semibold text-sm flex items-center gap-1">
                  Read More <i className="ri-arrow-right-line"></i>
                </span>
              </a>

              <a href="/locations/new-york-to-ireland" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover-lift group">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-map-pin-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0A381F] transition-colors">
                  Moving from New York to Ireland
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Complete guide for New Yorkers relocating to Ireland with cost comparisons
                </p>
                <span className="text-[#0A381F] font-semibold text-sm flex items-center gap-1">
                  Read More <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}